import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { UseMutationResult } from '@tanstack/react-query'
import { Money, Currencies } from 'ts-money'
import { IInvoice, TCFDIVersion } from 'models/invoices.models'
import { getSafeDecimalValue } from 'lib/helpers/shared.helpers'
import {
  selectActiveWorkspace,
  selectCatalogue,
  selectCreatePaymentComplement,
} from 'lib/helpers/selectors'
import useLoader from 'hooks/Shared/useLoader'
import useAddPaymentComplement from 'hooks/Invoices/PaymentComplements/useAddPaymentComplement'
import { useTaxCatalogue } from 'hooks/queries/catalogues'
import { useCfdi, useCfdiPaidAmount } from 'hooks/queries/invoices'
import { useAppSelector } from 'store/hooks'
import { storeFormValue } from 'actions/forms'
import useDialog from 'hooks/Shared/useDialog'

interface IUseAddPaymentComplementContainerProps {
  invoice: IInvoice | null
  invoiceId: number
  version: TCFDIVersion
  cleanup: () => void
}

export interface IUseAddPaymentComplementContainer {
  valid: boolean
  addMutation: UseMutationResult<void, any, void>
  isa: Money
  ip: Money
  monto: Money
  tasaCat:
    | number[]
    | {
        min: number
        max: number
      }
    | null
    | undefined
  tax: Money
  saveValueToStore: (name: string, value: any) => void
  setValid: Dispatch<SetStateAction<boolean>>
}

const useAddPaymentComplementContainer = ({
  invoice,
  invoiceId,
  version,
  cleanup,
}: IUseAddPaymentComplementContainerProps): IUseAddPaymentComplementContainer => {
  const dispatch = useDispatch()

  const dialog = useDialog()
  const loader = useLoader()

  const activeWorkspace = useAppSelector(selectActiveWorkspace)
  const form = useAppSelector(selectCreatePaymentComplement)
  const monedas = useAppSelector(selectCatalogue('monedas'))

  const [valid, setValid] = useState(false)

  const monto = Money.fromDecimal(
    getSafeDecimalValue(form.monto),
    Currencies.MXN
  )

  const tax = monto.multiply(Number(form?.tasa) || 0)

  const ip =
    version === '3.3'
      ? Money.fromDecimal(
          getSafeDecimalValue(form.importePagado),
          Currencies.MXN
        )
      : monto.add(tax)

  const {
    isFetching: isFetchingInvoice,
    isLoading: isLoadingInvoice,
    isError: isInvoiceError,
    refetch: refetchInvoice,
  } = useCfdi(activeWorkspace, invoiceId)

  const {
    data: taxesData,
    isFetching: isFetchingTaxes,
    isLoading: isLoadingTaxes,
    isError: isTaxesError,
    refetch: refetchTaxes,
  } = useTaxCatalogue({ enabled: version === '4.0' })

  const {
    data: paidAmountData,
    isFetching: isFetchingPaidAmount,
    isLoading: isLoadingPaidAmount,
    isError: isPaidAmountError,
    refetch: refetchPaidAmount,
  } = useCfdiPaidAmount(activeWorkspace, invoice, {
    enabled: !!invoice && version === '4.0',
    // onSuccess: (data) => {
    //   const isa = Money.fromDecimal(invoice?.total || 0, Currencies.MXN).subtract(data);
    //   saveValueToStore("importeSaldoAnterior", isa.toString());
    // }
  })

  const tasaCat = taxesData ? taxesData[0].value : []

  const paidAmount = paidAmountData ?? Money.fromDecimal(0, Currencies.MXN)

  const addMutation = useAddPaymentComplement({
    activeWorkspace,
    invoice,
    version,
    onSuccess: cleanup,
  })

  const isa =
    version === '3.3'
      ? Money.fromDecimal(
          getSafeDecimalValue(form.importeSaldoAnterior),
          Currencies.MXN
        )
      : Money.fromDecimal(invoice?.total || 0, Currencies.MXN).subtract(
          paidAmount
        )

  const saveValueToStore = (name: string, value: any) => {
    dispatch(storeFormValue('createPaymentComplement', name, value))
  }

  const queriesLoading = useMemo(
    () => [
      isFetchingInvoice,
      isLoadingInvoice,
      isFetchingPaidAmount,
      isLoadingPaidAmount,
      isFetchingTaxes,
      isLoadingTaxes,
    ],
    [
      isFetchingInvoice,
      isLoadingInvoice,
      isFetchingPaidAmount,
      isLoadingPaidAmount,
      isFetchingTaxes,
      isLoadingTaxes,
    ]
  )

  const queriesError = useMemo(
    () => [isInvoiceError, isPaidAmountError, isTaxesError],
    [isInvoiceError, isPaidAmountError, isTaxesError]
  )

  useEffect(() => {
    if (!form.moneda) {
      const mxn = monedas.filter(m => m.value === 'MXN')[0]
      saveValueToStore('moneda', mxn)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    queriesLoading.some(l => l)
      ? loader.primary('Cargando información del comprobante')
      : loader.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queriesLoading])

  useEffect(() => {
    if (queriesError.some(e => e)) {
      dialog.danger({
        subtitle: 'No se pudo obtener la información del CFDI.',
        primaryButtonText: 'Reintentar',
        secondaryButtonText: 'Cancelar',
        onPrimaryClick: () => {
          refetchInvoice()
          refetchPaidAmount()
          refetchTaxes()
        },
        onSecondaryClick: cleanup,
      })
    }
  }, [
    dialog,
    queriesError,
    addMutation,
    cleanup,
    refetchInvoice,
    refetchPaidAmount,
    refetchTaxes,
  ])

  return {
    valid,
    addMutation,
    isa,
    ip,
    monto,
    tasaCat,
    tax,
    saveValueToStore,
    setValid,
  }
}

export default useAddPaymentComplementContainer
